import { EventDescription, type EventDate } from './types/event'
import type { CartItem } from './types/cart'

export const useEmbeddedCheckout = () => {
  const loading = ref(false)
  const router = useRouter()
  const supplierIdParam = computed(
    () => router.currentRoute.value.query.supplierId
  )
  const eventIdParam = computed(() => router.currentRoute.value.query.eventId)
  const quantityParam = computed(() => router.currentRoute.value.query.quantity)
  const eventTypeParam = computed(() => router.currentRoute.value.query.type)
  const eventDescriptionIdParam = computed(
    () => router.currentRoute.value.query.eventDescriptionId
  )
  const isEmbedded = computed(
    () => router.currentRoute.value.query.embedded === 'true'
  )
  const isPrivateParam = computed(() => router.currentRoute.value.query.private)

  const isGiftcard = computed(() => eventTypeParam.value === 'giftcard')
  const isPrivate = computed(() => isPrivateParam.value === 'true')

  const { addItem, clear } = useCart('embedded-instance')

  const fetchEventDate = (eventId: string) => {
    return kftApiRequest<EventDate[]>(
      `/v1/store/events/${eventDescriptionIdParam.value}/dates?search=id:${eventId}`,
      {
        method: 'GET',
      }
    )
  }

  const hasValidParams = computed(() => {
    return (
      eventIdParam.value && quantityParam.value && eventDescriptionIdParam.value
    )
  })

  const fetchEventDescription = (eventDescriptionId: string) => {
    return kftApiRequest<EventDescription>(
      `/v1/store/events/id-${eventDescriptionId}`,
      {
        method: 'GET',
      }
    )
  }

  const fetchEventData = async () => {
    if (!hasValidParams.value) {
      return null
    }

    const [eventDescriptionResponse, eventDateResponse] = await Promise.all([
      fetchEventDescription(eventDescriptionIdParam.value!.toString()),
      fetchEventDate(eventIdParam.value!.toString()),
    ])

    const eventDescription = eventDescriptionResponse.data

    const eventDate = eventDateResponse.data.find(
      (date) => date.id === eventIdParam.value
    )

    console.log('eventDescription', eventDescription)
    console.log('eventDate', eventDate)

    if (!eventDescription) {
      return null
    }

    return {
      eventDescription,
      eventDate,
    }
  }

  const getEventCartItem = (
    eventDescription: EventDescription,
    eventDate?: EventDate
  ) => {
    if (!eventDate) {
      return null
    }

    const cartItem = parseEventDateToCartItem(
      eventDescription,
      Number(quantityParam.value),
      ref(eventDate),
      ref(undefined),
      isPrivate.value
    )

    return cartItem
  }

  const getGiftcardCartItem = (
    eventDescription: EventDescription,
    eventDate?: EventDate
  ) => {
    return parseGiftcardToCartItem(
      eventDescription,
      Number(quantityParam.value),
      eventDate ? ref(eventDate) : undefined,
      'EVENT'
    )
  }

  /**
   * @name addEventTicketToCart
   * @desc Add the event or giftcard to the cart
   * */
  const addEventTicketToCart = async () => {
    if (!hasValidParams.value) {
      return
    }

    loading.value = true

    // Clear the cart after checking if the params are valid
    clear()

    try {
      const eventData = await fetchEventData()

      if (!eventData) {
        loading.value = false
        return
      }

      const { eventDescription, eventDate } = eventData

      const itemObj = isGiftcard.value
        ? getGiftcardCartItem(eventDescription, eventDate)
        : getEventCartItem(eventDescription, eventDate)

      if (!itemObj) {
        loading.value = false
        return
      }

      addItem(itemObj as unknown as CartItem)
    } catch (error) {
      console.error('Error adding event ticket to cart:', error)
    } finally {
      loading.value = false
    }
  }

  return {
    /* Variables */
    supplierIdParam,
    eventIdParam,
    quantityParam,
    eventTypeParam,
    eventDescriptionIdParam,
    isEmbedded,
    isGiftcard,
    isPrivate,
    loading,

    /* Methods */
    fetchEventData,
    addEventTicketToCart,
  }
}
